import { Component, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyCTA from "@/components/StickyCTA";
import Home from "@/pages/Home";
import OurStory from "@/pages/OurStory";
import Menu from "@/pages/Menu";
import RipeNow from "@/pages/RipeNow";
import Stores from "@/pages/Stores";
import Franchise from "@/pages/Franchise";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Admin from "@/pages/Admin";
import { track } from "@/lib/track";

class ErrorBoundary extends Component {
  state = { err: false };
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen grid place-items-center bg-cream px-8 text-center">
          <div>
            <p className="font-display text-3xl font-semibold text-deep">Something went wrong.</p>
            <button
              data-testid="error-recover-button"
              onClick={() => { this.setState({ err: false }); window.location.href = "/"; }}
              className="mt-6 rounded-full bg-orange-rific px-8 py-3 font-body text-sm font-bold uppercase tracking-widest text-white"
            >
              Back to RIFIC
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/* GA4 SPA page tracking: fires one page_view per route change, including first load. */
function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    track("page_view", {
      page_title: document.title,
      page_path: pathname,
      page_location: window.location.href,
    });
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
}

function TopChrome() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return <Header />;
}

function BottomChrome() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return (
    <>
      <Footer />
      <StickyCTA />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SmoothScroll />
        <ScrollToTop />
        <RouteTracker />
        <div className="flex min-h-screen flex-col overflow-x-clip">
          <TopChrome />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/our-story" element={<OurStory />} />
              <Route path="/menu" element={<Menu />} />
              <Route path="/ripe-now" element={<RipeNow />} />
              <Route path="/stores" element={<Stores />} />
              <Route path="/franchise" element={<Franchise />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/admin/*" element={<Admin />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </main>
          <BottomChrome />
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
